import React, { useState, useEffect } from "react";
import { useParams, useLocation } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Button,
  CircularProgress,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from "@mui/material";
import ReactMarkdown from "react-markdown";
import {
  getNotebook,
  addComment,
  getComments,
  updateComment,
  deleteComment,
  generateShareLink,
  getTeacherNotes,
  saveTeacherNotes,
  getLectureDataset,
  addCommentToDataset,
  removeCommentFromDataset,
  getCommentDatasets,
  sendNotebookCheckTime,
} from "../services/api";
import { Notebook, Comment, Dataset } from "../types";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import EditNoteIcon from "@mui/icons-material/EditNote";
import Fab from "@mui/material/Fab";
import Zoom from "@mui/material/Zoom";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import "./NotebookPage.css";
import markdownComponents from "../components/MarkdownComponents";
import { NotebookViewer } from "../components/NotebookViewer";

// Добавляем новый компонент для диалога редактирования заметок преподавателя
interface TeacherNotesDialogProps {
  open: boolean;
  initialValue: string;
  onClose: () => void;
  onSave: (notes: string) => void;
}

const TeacherNotesDialog: React.FC<TeacherNotesDialogProps> = ({
  open,
  initialValue,
  onClose,
  onSave,
}) => {
  // Используем локальное состояние для редактирования, не связанное с основным компонентом
  const [localNotes, setLocalNotes] = useState(initialValue);
  // Добавляем состояние для переключения между режимами редактирования и предпросмотра
  const [previewMode, setPreviewMode] = useState(false);

  // Сбрасываем локальное состояние при открытии диалога
  useEffect(() => {
    setLocalNotes(initialValue);
    setPreviewMode(false); // Сбрасываем режим предпросмотра при открытии диалога
  }, [initialValue, open]);

  const handleSave = () => {
    onSave(localNotes);
  };

  // Предотвращаем распространение событий клавиатуры и колеса мыши
  const preventPropagation = (e: React.SyntheticEvent) => {
    e.stopPropagation();
  };

  // Функция для переключения режима предпросмотра
  const togglePreviewMode = () => {
    setPreviewMode(!previewMode);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
      // Предотвращаем распространение событий прокрутки и клавиатуры
      onKeyDown={preventPropagation}
      onWheel={preventPropagation}
      onClick={preventPropagation}
      sx={{ position: "fixed" }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <span>Редактирование заметок преподавателя</span>
        <IconButton
          onClick={togglePreviewMode}
          color="primary"
          title={previewMode ? "Режим редактирования" : "Режим предпросмотра"}
        >
          {previewMode ? <VisibilityOffIcon /> : <VisibilityIcon />}
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {previewMode ? (
          // Режим предпросмотра
          <Box
            sx={{
              mt: 2,
              p: 2,
              border: "1px solid #e0e0e0",
              borderRadius: 1,
              minHeight: "200px",
            }}
          >
            <ReactMarkdown components={markdownComponents}>
              {localNotes || "Нет заметок"}
            </ReactMarkdown>
          </Box>
        ) : (
          // Режим редактирования
          <TextField
            autoFocus
            margin="dense"
            multiline
            rows={10}
            value={localNotes}
            onChange={(e) => setLocalNotes(e.target.value)}
            fullWidth
            variant="outlined"
            placeholder="Введите заметки о баллах..."
            // Отключаем автокоррекцию и проверку орфографии
            autoComplete="off"
            autoCorrect="off"
            spellCheck="false"
            // Предотвращаем распространение событий клавиатуры
            onKeyDown={preventPropagation}
            // Предотвращаем прокрутку страницы при вводе
            onFocus={preventPropagation}
            sx={{
              mt: 2,
              "& .MuiInputBase-root": {
                fontFamily: "monospace",
              },
            }}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Отмена
        </Button>
        <Button
          onClick={handleSave}
          color="primary"
          variant="contained"
          startIcon={<SaveIcon />}
        >
          Сохранить
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export const NotebookPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const location = useLocation();
  const [notebook, setNotebook] = useState<Notebook | null>(null);
  const [comments, setComments] = useState<Comment[]>([]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [shareLink, setShareLink] = useState<string | null>(null);
  const [isShareDialogOpen, setIsShareDialogOpen] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);

  // Добавляем состояние для карточки с заметками преподавателя
  const [teacherNotes, setTeacherNotes] = useState<string>("");
  // Заменяем режим редактирования на диалоговое окно
  const [isTeacherNotesDialogOpen, setIsTeacherNotesDialogOpen] =
    useState<boolean>(false);
  // Добавляем состояние для просмотра заметок
  const [isViewTeacherNotesDialogOpen, setIsViewTeacherNotesDialogOpen] =
    useState<boolean>(false);
  const [dataset, setDataset] = useState<Dataset | null>(null);
  const [commentDatasets, setCommentDatasets] = useState<
    Record<number, boolean>
  >({});

  // Проверяем, является ли текущий просмотр режимом "только чтение"
  const urlParams = new URLSearchParams(location.search);
  const viewToken = urlParams.get("viewToken");
  const isReadOnlyMode = !!viewToken;

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (id) {
          const [notebookData, commentsData] = await Promise.all([
            getNotebook(parseInt(id)),
            getComments(parseInt(id), viewToken),
          ]);
          setNotebook(notebookData);
          setComments(commentsData);

          // Получаем заметки преподавателя
          try {
            const notes = await getTeacherNotes(parseInt(id));
            setTeacherNotes(notes);
          } catch (error) {
            console.error("Ошибка при получении заметок преподавателя:", error);
            setTeacherNotes("");
          }

          // Получаем датасет лекции, если есть
          if (notebookData.lectureId) {
            try {
              const datasetData = await getLectureDataset(
                notebookData.lectureId
              );
              setDataset(datasetData);

              // Если есть датасет, получаем информацию о комментариях в нем
              if (datasetData) {
                const commentDatasetsMap: Record<number, boolean> = {};

                // Для каждого комментария проверяем, находится ли он в датасете
                for (const comment of commentsData) {
                  try {
                    const commentDatasets = await getCommentDatasets(
                      comment.id
                    );
                    commentDatasetsMap[comment.id] = commentDatasets.some(
                      (d) => d.id === datasetData.id
                    );
                  } catch (error) {
                    console.error(
                      `Ошибка при получении датасетов для комментария ${comment.id}:`,
                      error
                    );
                    commentDatasetsMap[comment.id] = false;
                  }
                }

                setCommentDatasets(commentDatasetsMap);
              }
            } catch (error) {
              console.error("Ошибка при получении датасета лекции:", error);
              setDataset(null);
            }
          }

          // Автоматически генерируем ссылку при загрузке страницы
          if (!isReadOnlyMode) {
            try {
              const response = await generateShareLink(parseInt(id));
              const baseUrl = window.location.origin;
              const fullShareLink = `${baseUrl}/review/${response.token}`;
              setShareLink(fullShareLink);
            } catch (err) {
              console.error("Ошибка при автоматической генерации ссылки:", err);
            }
          }
        }
      } catch (err) {
        setError("Ошибка при загрузке ноутбука");
        console.error("Ошибка при загрузке данных:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [id, viewToken, isReadOnlyMode]);

  useEffect(() => {
    if (!isReadOnlyMode) {
      let isActive = true;
      let totalActiveTime = 0;
      let lastActivityTime = Date.now();
      let lastResumeTime = Date.now();

      const INACTIVITY_LIMIT = 20000; // 20 сек
      const SEND_INTERVAL = 5000; // 5 сек

      const activityHandler = () => {
        const now = Date.now();
        if (!isActive && now - lastActivityTime > INACTIVITY_LIMIT) {
          lastResumeTime = now;
          isActive = true;
        }
        lastActivityTime = now;
      };

      const checkInactivity = () => {
        const now = Date.now();
        if (isActive && now - lastActivityTime > INACTIVITY_LIMIT) {
          totalActiveTime += now - lastResumeTime;
          isActive = false;
        }
      };

      const sendIfNeeded = () => {
        const now = Date.now();

        if (isActive) {
          totalActiveTime += now - lastResumeTime;
          lastResumeTime = now;
        }

        if (totalActiveTime >= 1000 && id) {
          const payload = totalActiveTime;
          totalActiveTime = 0;
          sendNotebookCheckTime(parseInt(id), payload).catch((err) => {
            console.error("Ошибка при отправке времени:", err);
            totalActiveTime += payload; // восстановим в случае ошибки
          });
        }
      };

      const handleBeforeUnload = () => {
        if (isActive) {
          totalActiveTime += Date.now() - lastResumeTime;
        }

        if (id && totalActiveTime >= 1000) {
          sendNotebookCheckTime(parseInt(id), totalActiveTime);
        }
      };

      const events = ["mousemove", "keydown", "scroll", "touchstart"];
      events.forEach((event) =>
        window.addEventListener(event, activityHandler, { passive: true })
      );

      const inactivityInterval = setInterval(checkInactivity, 5000);
      const sendInterval = setInterval(sendIfNeeded, SEND_INTERVAL);
      window.addEventListener("beforeunload", handleBeforeUnload);

      return () => {
        if (isActive) {
          totalActiveTime += Date.now() - lastResumeTime;
        }

        sendIfNeeded();

        events.forEach((event) =>
          window.removeEventListener(event, activityHandler)
        );
        clearInterval(inactivityInterval);
        clearInterval(sendInterval);
        window.removeEventListener("beforeunload", handleBeforeUnload);
      };
    }
  }, [id, isReadOnlyMode]);

  const handleCommentSubmit = async (commentToCreate: Comment) => {
    try {
      // Создаем новый комментарий
      const newComment = await addComment(parseInt(id!), {
        text: commentToCreate.text,
        cellId: commentToCreate.cellId,
        lineNumber: commentToCreate.lineNumber,
      });

      // Добавляем новый комментарий в список
      setComments([...comments, newComment]);

      // Если есть датасет, инициализируем информацию о нахождении комментария в датасете
      if (dataset) {
        setCommentDatasets((prev) => ({
          ...prev,
          [newComment.id]: false,
        }));
      }
      return newComment;
    } catch (error) {
      console.error("Ошибка при создании комментария:", error);
      return null;
    }
  };

  const handleDeleteComment = async (commentId: number) => {
    if (!window.confirm("Удалить комментарий?")) return;

    try {
      await deleteComment(parseInt(id!), commentId);
      setComments(comments.filter((c: Comment) => c.id !== commentId));
    } catch (err) {
      setError("Ошибка при удалении комментария");
      console.error(err);
    }
  };

  // Добавляем функцию handleSaveComment для обновления комментария
  const handleEditComment = async (commentId: number, text: string) => {
    try {
      const updatedComment = await updateComment(
        parseInt(id!),
        commentId,
        text
      );

      // Обновляем массив комментариев
      const updatedComments = comments.map((c: Comment) =>
        c.id === commentId ? updatedComment : c
      );
      setComments(updatedComments);
      return updatedComment;
    } catch (err) {
      console.error("Ошибка при обновлении комментария:", err);
      setError("Ошибка при обновлении комментария");
      return null;
    }
  };

  const handleGenerateShareLink = async () => {
    try {
      if (id) {
        const response = await generateShareLink(parseInt(id));
        const baseUrl = window.location.origin;
        const fullShareLink = `${baseUrl}/review/${response.token}`;
        setShareLink(fullShareLink);

        // Копируем ссылку в буфер обмена
        navigator.clipboard
          .writeText(fullShareLink)
          .then(() => {
            setCopySuccess(true);
            setTimeout(() => setCopySuccess(false), 2000);
          })
          .catch((err) => {
            console.error("Не удалось скопировать ссылку: ", err);
          });
      }
    } catch (err) {
      setError("Ошибка при генерации ссылки");
      console.error(err);
    }
  };

  const handleCopyShareLink = () => {
    if (shareLink) {
      navigator.clipboard
        .writeText(shareLink)
        .then(() => {
          setCopySuccess(true);
          setTimeout(() => setCopySuccess(false), 2000);
        })
        .catch((err) => {
          console.error("Не удалось скопировать ссылку: ", err);
        });
    }
  };

  // Функция для открытия диалога редактирования заметок
  const handleOpenTeacherNotesDialog = () => {
    setIsTeacherNotesDialogOpen(true);
  };

  // Функция для закрытия диалога редактирования заметок без сохранения
  const handleCloseTeacherNotesDialog = () => {
    setIsTeacherNotesDialogOpen(false);
  };

  // Функция для сохранения заметок преподавателя
  const handleSaveTeacherNotes = async (newNotes: string) => {
    try {
      if (!id) {
        setError("ID ноутбука не определен");
        return;
      }

      // Сохраняем заметки через API
      const updatedNotes = await saveTeacherNotes(parseInt(id), newNotes);

      // Обновляем состояние
      setTeacherNotes(updatedNotes);

      // Закрываем диалог
      setIsTeacherNotesDialogOpen(false);

      // Показываем уведомление об успешном сохранении
    } catch (err) {
      console.error("Ошибка при сохранении заметок преподавателя:", err);
      setError("Ошибка при сохранении заметок преподавателя");
    }
  };

  // Функция для открытия диалога просмотра заметок
  const handleOpenViewTeacherNotesDialog = () => {
    setIsViewTeacherNotesDialogOpen(true);
  };

  // Функция для закрытия диалога просмотра заметок
  const handleCloseViewTeacherNotesDialog = () => {
    setIsViewTeacherNotesDialogOpen(false);
  };

  // Функция для добавления комментария в датасет
  const handleAddCommentToDataset = async (commentId: number) => {
    if (!dataset) return;

    try {
      await addCommentToDataset(dataset.id, commentId);

      // Обновляем состояние
      setCommentDatasets((prevDatasets) => ({
        ...prevDatasets,
        [commentId]: true,
      }));
    } catch (error) {
      console.error(
        `Ошибка при добавлении комментария ${commentId} в датасет ${dataset.id}:`,
        error
      );
    }
  };

  // Функция для удаления комментария из датасета
  const handleRemoveCommentFromDataset = async (commentId: number) => {
    if (!dataset) return;

    try {
      await removeCommentFromDataset(dataset.id, commentId);

      // Обновляем состояние
      setCommentDatasets((prevDatasets) => ({
        ...prevDatasets,
        [commentId]: false,
      }));
    } catch (error) {
      console.error(
        `Ошибка при удалении комментария ${commentId} из датасета ${dataset.id}:`,
        error
      );
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container>
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      </Container>
    );
  }

  if (!notebook) {
    return (
      <Container>
        <Alert severity="error" sx={{ mt: 2 }}>
          Ноутбук не найден
        </Alert>
      </Container>
    );
  }

  return (
    <>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box
          sx={{
            mb: 2,
            display: "flex",
            gap: 1,
            flexWrap: "wrap",
            alignItems: "center",
          }}
        >
          {!isReadOnlyMode && (
            <>
              <Button
                variant="outlined"
                startIcon={<EditIcon />}
                onClick={() => {
                  if (shareLink) {
                    const token = shareLink.split("/").pop();
                    window.open(`/share/${token}`, "_blank");
                  } else {
                    handleGenerateShareLink().then(() => {
                      if (shareLink) {
                        const token = shareLink.split("/").pop();
                        window.open(`/share/${token}`, "_blank");
                      }
                    });
                  }
                }}
                sx={{ mr: 2 }}
              >
                Ноутбук с ревью для студента
              </Button>

              {copySuccess && (
                <Typography variant="caption" sx={{ color: "success.main" }}>
                  Ссылка скопирована!
                </Typography>
              )}
            </>
          )}
          {isReadOnlyMode && (
            <Typography
              variant="body2"
              sx={{
                ml: 2,
                display: "flex",
                alignItems: "center",
                color: "text.secondary",
              }}
            >
              Режим просмотра (только чтение)
            </Typography>
          )}
        </Box>

        <NotebookViewer
          notebook={notebook}
          comments={comments}
          onEditComment={handleEditComment}
          onDeleteComment={handleDeleteComment}
          onCreateComment={handleCommentSubmit}
          addCommentToDataset={handleAddCommentToDataset}
          removeCommentFromDataset={handleRemoveCommentFromDataset}
          isReadOnly={false}
          dataset={dataset}
          commentDatasets={commentDatasets}
        />
      </Container>

      <Dialog
        open={isShareDialogOpen}
        onClose={() => setIsShareDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Ссылка для студента</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            value={shareLink || ""}
            variant="outlined"
            InputProps={{
              readOnly: true,
            }}
            sx={{ mt: 2 }}
          />
          <Typography
            variant="caption"
            sx={{ mt: 1, display: "block", color: "text.secondary" }}
          >
            По этой ссылке студент сможет просматривать комментарии, но не
            сможет их изменять
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsShareDialogOpen(false)}>Закрыть</Button>
          <Button
            onClick={handleCopyShareLink}
            variant="contained"
            color="primary"
          >
            Копировать ссылку
          </Button>
        </DialogActions>
      </Dialog>

      {/* Используем новый компонент для диалога редактирования заметок */}
      <TeacherNotesDialog
        open={isTeacherNotesDialogOpen}
        initialValue={teacherNotes}
        onClose={handleCloseTeacherNotesDialog}
        onSave={handleSaveTeacherNotes}
      />

      {/* Добавляем диалог для просмотра заметок */}
      <Dialog
        open={isViewTeacherNotesDialogOpen}
        onClose={handleCloseViewTeacherNotesDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Заметки преподавателя</DialogTitle>
        <DialogContent>
          <Box
            sx={{
              p: 2,
              backgroundColor: "background.paper",
              borderRadius: 1,
              minHeight: "100px",
            }}
          >
            {teacherNotes ? (
              <ReactMarkdown components={markdownComponents}>
                {teacherNotes}
              </ReactMarkdown>
            ) : (
              <Typography color="text.secondary" variant="body2">
                Нет заметок преподавателя
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          {!isReadOnlyMode && (
            <Button
              onClick={() => {
                handleCloseViewTeacherNotesDialog();
                handleOpenTeacherNotesDialog();
              }}
              startIcon={<EditIcon />}
            >
              Редактировать
            </Button>
          )}
          <Button onClick={handleCloseViewTeacherNotesDialog}>Закрыть</Button>
        </DialogActions>
      </Dialog>

      {/* TODO come back to that and dispaly it only if there's nto active comment? */}
      {/* Добавляем плавающую кнопку для заметок преподавателя */}
      <Zoom in={true}>
        {/* <Zoom in={!tempComment}> */}
        <Fab
          color="primary"
          aria-label="edit"
          onClick={
            !isReadOnlyMode
              ? handleOpenTeacherNotesDialog
              : handleOpenViewTeacherNotesDialog
          }
          sx={{
            position: "fixed",
            bottom: 20,
            right: 20,
            zIndex: 1000,
          }}
        >
          <EditNoteIcon />
        </Fab>
      </Zoom>
    </>
  );
};
